'use client';

import { SliceComponentProps } from '@prismicio/react';
import classNames from 'classnames';
import React from 'react';
import EventNames from 'src/analytics/EventNames';
import { trackClick } from 'src/analytics/Events';
import { Button } from '@get-mosh/msh-shared';
import Heading from 'src/components/Heading/Heading';
import styles from './hero.module.scss';
import getContentGroups from 'src/helpers/getContentGroups';
import List from 'src/components/List/List';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { Content } from '@prismicio/client';
import { Vertical } from 'src/constants/Verticals';

type HeroProps = SliceComponentProps<Content.HeroSlice> & {
    context: { pageType: string };
};

const Hero = ({ slice, context }: HeroProps) => (
    <section>
        <div
            className={classNames('hero-generic', styles['section'])}
            data-hero
        >
            {slice?.primary?.hero_image?.url && (
                <img
                    src={slice.primary.hero_image.url}
                    alt={slice.primary.hero_image.alt ?? 'image'}
                />
            )}

            <div className="md:absolute md:left-0 md:top-0 md:h-full md:w-full">
                <div className="container relative mx-auto md:h-full">
                    <div className={classNames(styles['content'])}>
                        {slice?.primary?.hero_title && (
                            <Heading
                                level="h1"
                                tag="h"
                                headerStyle={{
                                    color: slice?.primary?.hero_title_color
                                        ? slice.primary.hero_title_color
                                        : undefined,
                                    backgroundColor: slice?.primary
                                        ?.hero_title_bg_color
                                        ? slice.primary.hero_title_bg_color
                                        : undefined,
                                }}
                            >
                                {slice.primary.hero_title}
                            </Heading>
                        )}
                        {slice?.primary?.hero_description && (
                            <p
                                className={styles['description']}
                                style={{
                                    color: slice?.primary
                                        ?.hero_description_color
                                        ? slice.primary.hero_description_color
                                        : undefined,
                                    backgroundColor: slice?.primary
                                        ?.hero_description_bg_color
                                        ? slice.primary
                                              .hero_description_bg_color
                                        : undefined,
                                }}
                            >
                                {slice.primary.hero_description}
                            </p>
                        )}
                        {slice?.primary?.hero_content && (
                            <div className={styles['description']}>
                                <HeroContent
                                    content={slice.primary.hero_content}
                                />
                            </div>
                        )}
                        <Button
                            text={slice.primary.hero_cta || 'Learn More'}
                            as="a"
                            href={slice.primary.hero_link_path || '/'}
                            onClick={() => {
                                trackClick(EventNames.BUTTON_CLICKED, {
                                    pageType: context.pageType,
                                    vertical: slice.primary
                                        .vertical as Vertical,
                                    label:
                                        slice.primary.hero_cta || 'Learn More',
                                    link: slice.primary.hero_link_path || '/',
                                    variation: slice.variation,
                                    location: slice.slice_type,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const HeroContent = ({ content }) => {
    const contentGroups = getContentGroups(content);
    return (
        <>
            {contentGroups.map((e, i) =>
                // This logic right here is very simple, we only allow for 2 types of content, either a paragraph or a list
                e.every((e) => e.type === 'list-item') ? (
                    <List
                        className={'mb-4'}
                        items={e}
                        key={i}
                        icon={{
                            faIcon: faCircleCheck,
                            background: 'bg-none',
                            colour: 'text-black',
                            border: 'border-none',
                        }}
                    />
                ) : (
                    <p key={i}>{e[0].text}</p>
                ),
            )}
        </>
    );
};

export default Hero;
